import React, { useState, useEffect, useCallback, useContext, useRef } from 'react'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons'
import { formatValueByColumn, formatDateTimeValue } from '../helpers/chart-formatters'
import { apiGet } from 'api'
import AuthContext from 'context/auth-context'
import { isNull, isUndefined } from 'lodash'
import SearchableColumnSelect from './searchable-column-select'
import {
  DecimalPlacesOption,
  SegmentDisplayOption,
  SEGMENT_DISPLAY_DEFAULT,
  DateFormatOption,
  DATE_FORMAT_DEFAULT
} from './chart-options'
import InlineFetchError from 'shared/inline-fetch-error'
// Aliased: `dataTableConfig.renderOptions` below takes a `getColumnDisplayName` prop.
import {
  getColumnDisplayName as resolveColumnDisplayName,
  hasSegmentDisplayChoice
} from 'dashboard/utils'

// Only these display types are numeric and should be routed through formatValueByColumn.
// percent and flag_count are excluded because formatValueByColumn has no handling for them
// and would incorrectly coerce them through formatNumber.
const NUMERIC_DISPLAY_TYPES = new Set(['money', 'two_digit_money', 'risk_score'])

const formatCell = (value, col) => {
  if (isUndefined(value) || isNull(value)) return ''
  if (NUMERIC_DISPLAY_TYPES.has(col.display_as)) {
    return formatValueByColumn(value, { display_as: col.display_as, decimal_places: col.decimal_places }, null)
  }
  // Date columns (and any stray ISO datetime strings) are rendered human-readably in the format
  // the column asked for, e.g. "2026-07-30T00:00:00.000Z" -> "July 30, 2026" or "07/30/2026".
  // formatDateTimeValue returns non-date values unchanged, so text / status / boolean cells fall
  // through as before.
  return formatDateTimeValue(value, col.date_format)
}

const DataTable = ({
  initialData,
  columns: initialColumns,
  hasMore: initialHasMore,
  totalCount: initialTotalCount,
  chartId,
  dataUrl,
  params,
  sortBy: initialSortBy,
  sortDirection: initialSortDirection,
  municipality,
  // The taxonomy this chart's rollup columns bucket by. Resolved by the chart card.
  groupingName = null,
  // Called with a row's transaction id and ThirdLine ID, to drill into the row.
  onRowClick = null
}) => {
  const token = useContext(AuthContext)
  const [allData, setAllData] = useState(initialData || [])
  const [columns, setColumns] = useState(initialColumns || [])
  const [offset, setOffset] = useState((initialData || []).length)
  const [hasMore, setHasMore] = useState(initialHasMore || false)
  const [totalCount, setTotalCount] = useState(initialTotalCount || 0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [sortBy, setSortBy] = useState(initialSortBy || null)
  const [sortDirection, setSortDirection] = useState(initialSortDirection || 'desc')
  const [isSorting, setIsSorting] = useState(false)
  // Load-more / sort failures used to be console-only, leaving the table looking like
  // nothing happened. Hold a message so it can be shown under the table too; the
  // rejection itself stays in the console rather than in front of the user.
  const [fetchError, setFetchError] = useState(null)
  // Incremented on every sort so in-flight load-more responses can be discarded.
  const sortGenerationRef = useRef(0)

  // Reset state when initial data changes (e.g. dashboard filter change)
  useEffect(() => {
    setAllData(initialData || [])
    setColumns(initialColumns || [])
    setOffset((initialData || []).length)
    setHasMore(initialHasMore || false)
    setTotalCount(initialTotalCount || 0)
    setSortBy(initialSortBy || null)
    setSortDirection(initialSortDirection || 'desc')
    setFetchError(null)
  }, [initialData, initialColumns, initialHasMore, initialTotalCount, initialSortBy, initialSortDirection])

  const buildDataTableParams = useCallback((dataTableOffset, sortColumn, sortDir) => {
    // Exclude columns param since the backend reads them from the DB
    const { columns: _columns, ...restParams } = params || {}
    return {
      ...restParams,
      data_table_offset: dataTableOffset,
      data_table_limit: 25,
      data_table_sort_by: sortColumn,
      data_table_sort_direction: sortDir
    }
  }, [params])

  const handleLoadMore = useCallback(async () => {
    if (!chartId || isLoadingMore || isSorting) return

    const generation = sortGenerationRef.current
    setIsLoadingMore(true)
    setFetchError(null)

    try {
      const result = await apiGet({
        url: dataUrl || `charts/${chartId}/data`,
        token,
        params: buildDataTableParams(offset, sortBy, sortDirection)
      })

      // Discard if a sort completed while this request was in-flight.
      if (sortGenerationRef.current !== generation) return

      setAllData(prevData => [...prevData, ...(result.data || [])])
      setOffset(prevOffset => prevOffset + (result.data || []).length)
      setHasMore(result.has_more || false)
    } catch (error) {
      console.error('Error loading more data table rows:', error)
      setFetchError("Couldn't load more rows")
    } finally {
      setIsLoadingMore(false)
    }
  }, [token, chartId, dataUrl, offset, buildDataTableParams, isLoadingMore, isSorting, sortBy, sortDirection])

  const handleSort = useCallback(async (columnKey) => {
    if (!chartId || isSorting) return

    const newDirection = sortBy === columnKey && sortDirection === 'desc' ? 'asc' : 'desc'

    sortGenerationRef.current += 1
    setIsSorting(true)
    setFetchError(null)

    try {
      const result = await apiGet({
        url: dataUrl || `charts/${chartId}/data`,
        token,
        params: buildDataTableParams(0, columnKey, newDirection)
      })

      setAllData(result.data || [])
      setOffset((result.data || []).length)
      setHasMore(result.has_more || false)
      setTotalCount(result.total_count || 0)
      setSortBy(columnKey)
      setSortDirection(newDirection)
    } catch (error) {
      console.error('Error sorting data table:', error)
      setFetchError("Couldn't sort this table")
    } finally {
      setIsSorting(false)
    }
  }, [chartId, dataUrl, token, buildDataTableParams, sortBy, sortDirection, isSorting])

  if (!allData.length) {
    return (
      <div className="text-center p-4">
        {fetchError ? <InlineFetchError message={fetchError} /> : <p>No data available</p>}
      </div>
    )
  }

  const renderSortIndicator = (columnKey) => {
    if (sortBy !== columnKey) {
      return <FontAwesomeIcon icon={faSort} className="ms-1" style={{ color: '#ccc' }} />
    }
    return sortDirection === 'desc'
      ? <FontAwesomeIcon icon={faSortDown} className="ms-1" style={{ color: '#555' }} />
      : <FontAwesomeIcon icon={faSortUp} className="ms-1" style={{ color: '#555' }} />
  }

  return (
    <div className="overflow-auto position-relative">
      {isSorting && (
        <div
          className="position-absolute d-flex align-items-center justify-content-center"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 20
          }}
        >
          <span className="text-muted">Sorting...</span>
        </div>
      )}

      <Table bordered size="sm" className="bg-white">
        <thead className="table-light">
          <tr>
            {/*
              Headers go through the shared resolver rather than rendering the stored
              TableColumn name. That name is one global value shared by every
              municipality, so a district that renamed seg_four to "Fund" saw "Segment
              Four" here while the column picker, the CSV export, and every other
              surface read "Fund".
            */}
            {columns.map((col) => (
              <th
                key={col.key}
                style={{ minWidth: '120px', cursor: 'pointer' }}
                onClick={() => handleSort(col.key)}
              >
                {resolveColumnDisplayName(col, municipality, groupingName)}
                {renderSortIndicator(col.key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allData.map((row, rowIndex) => {
            const transactionId = row._transaction_id
            const drillProps =
              onRowClick && transactionId
                ? {
                    onClick: () => onRowClick(transactionId, row._thirdline_id),
                    style: { cursor: 'pointer' },
                    title: 'Show transaction',
                    'data-testid': 'data-table-drill-row'
                  }
                : {}
            return (
              <tr key={transactionId ?? row.id ?? rowIndex} {...drillProps}>
                {columns.map((col) => (
                  <td key={`cell-${rowIndex}-${col.key}`} className="text-start">
                    {formatCell(row[col.key], col)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </Table>

      <div className="mt-2 text-muted small">
        Showing {allData.length} row{allData.length !== 1 ? 's' : ''}
        {totalCount > 0 ? ` of ${totalCount}` : ''}
      </div>

      {fetchError && (
        <div className="mt-2 text-center">
          <InlineFetchError message={fetchError} />
        </div>
      )}

      {hasMore && (
        <div className="mt-3 text-center">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  )
}

// Chart configuration for data tables
export const dataTableConfig = {
  validate: (formData) => {
    const errors = {}
    if (!formData.draftDataTableColumns || formData.draftDataTableColumns.length === 0) {
      errors.dataTableColumns = 'At least one column is required'
    }
    return errors
  },

  isQueryEnabled: (formData, hasChartId) => {
    return !!(
      hasChartId &&
      formData.savedDataTableColumns?.length > 0 &&
      formData.savedDataTableColumns.some((c) => c.table_column_id)
    )
  },

  buildChartParams: (formData) => ({
    columns: formData.draftDataTableColumns
      .map((c, index) => ({
        table_column_id: c.table_column_id,
        position: index,
        decimal_places: c.decimal_places === 2 ? 2 : 0,
        segment_display: c.segment_display || SEGMENT_DISPLAY_DEFAULT,
        date_format: c.date_format || DATE_FORMAT_DEFAULT
      }))
      .filter((c) => c.table_column_id)
  }),

  buildQueryParams: (baseParams, formData) => {
    return { ...baseParams }
  },

  renderOptions: ({
    Form,
    Button,
    formState,
    setters,
    validationErrors,
    hasSubmitted,
    getFilteredColumns,
    getColumnDisplayName,
    municipality,
    columns
  }) => {
    const { draftDataTableColumns } = formState
    const { setDraftDataTableColumns, setValidationErrors } = setters
    const availableColumns = getFilteredColumns()

    // The TableColumn a configured row points at, wherever it is listed. A saved column
    // can sit outside the filtered list (a different section's picker, say), so fall back
    // to the full set before giving up.
    const selectedColumnFor = (col) => {
      const id = String(col.table_column_id)
      return availableColumns.find((c) => String(c.id) === id) ||
        columns?.find((c) => String(c.id) === id)
    }

    // The chosen TableColumn when its cells can carry a format choice, undefined otherwise.
    // Only NUMERIC_DISPLAY_TYPES cells go through the number formatter — a text or date cell
    // would ignore it.
    const formattableColumnFor = (col) => {
      const selected = selectedColumnFor(col)
      return selected && NUMERIC_DISPLAY_TYPES.has(selected.display_as) ? selected : undefined
    }

    // Only the raw account segments carry a code alongside their description, so only they
    // get the description/code/both choice.
    const isSegmentColumn = (col) => hasSegmentDisplayChoice(selectedColumnFor(col))

    // Only date cells are rendered through the date formatter, so only they get the
    // choice of how that date reads.
    const isDateColumn = (col) => selectedColumnFor(col)?.display_as === 'date'

    return (
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Form.Label className="mb-0">Display Columns</Form.Label>
          {draftDataTableColumns.length < 15 && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setDraftDataTableColumns([
                  ...draftDataTableColumns,
                  {
                    table_column_id: '',
                    position: draftDataTableColumns.length,
                    decimal_places: 0,
                    segment_display: SEGMENT_DISPLAY_DEFAULT,
                    date_format: DATE_FORMAT_DEFAULT
                  }
                ])
              }}
            >
              + Add Column
            </Button>
          )}
        </div>

        {draftDataTableColumns.map((col, index) => (
          <div key={index} className="border rounded p-2 mb-2 bg-light">
            <div className="d-flex gap-2 align-items-center">
              <div className="flex-grow-1">
                <Form.Label className="small mb-1">Column {index + 1}</Form.Label>
                <SearchableColumnSelect
                  inputId={`data-table-column-select-${index}`}
                  columns={availableColumns}
                  value={col.table_column_id}
                  onChange={(val) => {
                    const newColumns = [...draftDataTableColumns]
                    newColumns[index] = { ...newColumns[index], table_column_id: val }
                    setDraftDataTableColumns(newColumns)
                    if (validationErrors.dataTableColumns) {
                      setValidationErrors((prev) => ({ ...prev, dataTableColumns: undefined }))
                    }
                  }}
                  getColumnDisplayName={getColumnDisplayName}
                  municipality={municipality}
                  isInvalid={hasSubmitted && !col.table_column_id}
                />
              </div>

              {isSegmentColumn(col) && (
                <SegmentDisplayOption
                  Form={Form}
                  inputId={`data-table-column-segment-display-${index}`}
                  segmentDisplay={col.segment_display}
                  setSegmentDisplay={(value) => {
                    const newColumns = [...draftDataTableColumns]
                    newColumns[index] = { ...newColumns[index], segment_display: value }
                    setDraftDataTableColumns(newColumns)
                  }}
                />
              )}

              {isDateColumn(col) && (
                <DateFormatOption
                  Form={Form}
                  inputId={`data-table-column-date-format-${index}`}
                  dateFormat={col.date_format}
                  setDateFormat={(value) => {
                    const newColumns = [...draftDataTableColumns]
                    newColumns[index] = { ...newColumns[index], date_format: value }
                    setDraftDataTableColumns(newColumns)
                  }}
                  label="Format"
                />
              )}

              {formattableColumnFor(col) && (
                <DecimalPlacesOption
                  Form={Form}
                  inputId={`data-table-column-decimals-${index}`}
                  displayAs={formattableColumnFor(col).display_as}
                  decimalPlaces={col.decimal_places}
                  setDecimalPlaces={(places) => {
                    const newColumns = [...draftDataTableColumns]
                    newColumns[index] = { ...newColumns[index], decimal_places: places }
                    setDraftDataTableColumns(newColumns)
                  }}
                  width="130px"
                />
              )}

              <div className="d-flex flex-column gap-1" style={{ width: '60px' }}>
                {index > 0 && (
                  <Button
                    variant="light-primary"
                    size="sm"
                    onClick={() => {
                      const newColumns = [...draftDataTableColumns]
                      const item = newColumns.splice(index, 1)[0]
                      newColumns.splice(index - 1, 0, item)
                      setDraftDataTableColumns(
                        newColumns.map((c, i) => ({ ...c, position: i }))
                      )
                    }}
                    style={{ fontSize: '0.7rem', padding: '0.1rem 0.3rem' }}
                  >
                    ↑
                  </Button>
                )}
                {index < draftDataTableColumns.length - 1 && (
                  <Button
                    variant="light-primary"
                    size="sm"
                    onClick={() => {
                      const newColumns = [...draftDataTableColumns]
                      const item = newColumns.splice(index, 1)[0]
                      newColumns.splice(index + 1, 0, item)
                      setDraftDataTableColumns(
                        newColumns.map((c, i) => ({ ...c, position: i }))
                      )
                    }}
                    style={{ fontSize: '0.7rem', padding: '0.1rem 0.3rem' }}
                  >
                    ↓
                  </Button>
                )}
              </div>

              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => {
                  const newColumns = draftDataTableColumns
                    .filter((_, i) => i !== index)
                    .map((c, i) => ({ ...c, position: i }))
                  setDraftDataTableColumns(newColumns)
                }}
                style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
              >
                ×
              </Button>
            </div>
          </div>
        ))}

        {draftDataTableColumns.length === 0 && (
          <div
            className={`text-center py-2 border border-dashed rounded ${
              hasSubmitted && validationErrors.dataTableColumns
                ? 'border-danger text-danger'
                : 'text-muted'
            }`}
          >
            <div className="small">
              No columns selected. Click "Add Column" to add your first column.
            </div>
            {hasSubmitted && validationErrors.dataTableColumns && (
              <div className="text-danger small mt-1">
                {validationErrors.dataTableColumns}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
}

export default DataTable
